import { useEvent } from '@emerald-works/react-event-bus-client'
import {
  Box,
  Checkbox,
  Collapse,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  Typography,
  useTheme
} from '@mui/material'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { BannerStatic } from '../../../components'
import { TenantEditSlice } from '../../../reducers'
import { useStyles } from './style'
import { useFeatureFormContext } from './feature-form-context'
import { KeyboardArrowRight } from '@mui/icons-material'
import {
  ActionButton,
  FormInputRow,
  Input
} from '@emerald-works-nova/components'
import { useForm } from 'react-hook-form'

const XapiSettings = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const dispatch = useDispatch()
  const { tenant } = useFeatureFormContext()
  const [isLoading, setIsLoading] = useState(false)
  const [sendSSOAttributes, setSendSSOAttributes] = useState(
    tenant.features?.xapi?.addSSOAttributesToStatement || false
  )
  const [statementTemplate, setStatementTemplate] = useState(
    tenant?.features?.xapi?.statementTemplate || 'mt3'
  )
  const [collapsed, setCollapsed] = useState(false)
  const [switchValue, setSwitchValue] = useState(
    tenant.features?.xapi?.enabled || false
  )
  const handleSwitch = (event) => {
    const value = event.target.checked

    if (collapsed && !value) {
      setCollapsed(false)
    }

    setSwitchValue(value)
  }

  const form = useForm({ mode: 'onChange' })
  const { errors, isValid } = form.formState
  const disableSave = isLoading || (switchValue && !isValid)

  const [tenantUpdate] = useEvent([
    {
      eventName: 'tenantUpdate',
      onSuccess: (tenant) => {
        BannerStatic.show({ label: 'xAPI configuration updated.' })
        setIsLoading(false)
        dispatch(TenantEditSlice.actions.updateTenantFeatures(tenant.features))
      },
      onError: () => {
        BannerStatic.show({
          label: 'There was an error updating the xAPI feature configuration',
          isError: true
        })
        setIsLoading(false)
      }
    }
  ])

  const handleSave = async () => {
    tenantUpdate.trigger({
      pk: tenant.pk,
      features: {
        ...tenant.features,
        xapi: {
          enabled: switchValue,
          addSSOAttributesToStatement: sendSSOAttributes,
          credentials: form.getValues(),
          statementTemplate
        }
      }
    })
    setIsLoading(true)
  }

  return (
    <Grid container className={classes.featContainer}>
      <Grid item xs={12}>
        <Box
          border={1}
          borderColor='#E0E0E0'
          display='flex'
          style={{
            justifyContent: 'space-between',
            flexDirection: 'column',
            padding: spacing(1, 4)
          }}
        >
          <Box display='flex' flexDirection='row'>
            <Typography variant='body2' style={{ marginTop: spacing(3.5) }}>
              xAPI Integration
            </Typography>

            <Box
              display='flex'
              style={{
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
                flex: 1,
                padding: spacing(1)
              }}
            >
              {switchValue && (
                <>
                  <Box>
                    <List component='nav' aria-label='xAPI configuration'>
                      <ListItemButton
                        aria-haspopup='true'
                        aria-controls='lock-menu'
                        aria-label='Toggle xAPI configuration'
                        onClick={() => setCollapsed((state) => !state)}
                      >
                        <ListItemText
                          disableTypography
                          style={{ display: 'flex', alignItems: 'center' }}
                        >
                          <Typography variant='body2'>
                            Configure credentials
                          </Typography>
                          <KeyboardArrowRight />
                        </ListItemText>
                      </ListItemButton>
                    </List>
                  </Box>
                  <Divider
                    orientation='vertical'
                    flexItem
                    style={{
                      margin: spacing(3, 0),
                      borderColor: 'rgba(0, 0, 0, 0.38)'
                    }}
                  />
                </>
              )}

              <Box style={{ padding: spacing(2, 0.5) }}>
                <Switch
                  color='primary'
                  checked={switchValue}
                  onChange={handleSwitch}
                  disabled={isLoading}
                />
              </Box>
            </Box>
          </Box>

          <Collapse in={collapsed}>
            <Box className={classes.formCtn}>
              <Grid item xs={12}>
                <FormInputRow
                  title='LRS Endpoint'
                  description='The LRS endpoint of the client integrating xAPI.'
                  smallFormTitles
                  InputComponent={
                    <Input
                      label='LRS Endpoint'
                      name='lrsEndpoint'
                      data-test='lrs-endpoint'
                      rules={{
                        required: true,
                        pattern:
                          /^(https?|ftp):\/\/(?:www\.)?[\w\\-]+(?:\.[\w\\-]+)*(?:\\:\d+)?(?:\/[^\s,]*)?$/
                      }}
                      valid={errors.lrsEndpoint === undefined}
                      error='LRS endpoint is not valid'
                      defaultValue={
                        tenant?.features?.xapi?.credentials?.lrsEndpoint ||
                      'http://'
                      }
                      control={form.control}
                      showErrors
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputRow
                  title='LRS Username'
                  description='The LRS username of the client integrating xAPI.'
                  smallFormTitles
                  InputComponent={
                    <Input
                      label='LRS Username'
                      name='lrsUsername'
                      data-test='lrs-username'
                      rules={{ required: true, minLength: 2 }}
                      valid={errors.lrsUsername === undefined}
                      error='LRS username should be at least 2 characters long.'
                      defaultValue={
                        tenant?.features?.xapi?.credentials?.lrsUsername
                      }
                      control={form.control}
                      showErrors
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputRow
                  title='LRS Password'
                  description='The LRS password of the client integrating xAPI.'
                  smallFormTitles
                  InputComponent={
                    <Input
                      label='LRS Password'
                      type='text'
                      name='lrsPassword'
                      data-test='lrs-password'
                      rules={{ required: true, minLength: 2 }}
                      valid={errors.lrsPassword === undefined}
                      error='LRS password should be at least 2 characters long.'
                      defaultValue={
                        tenant?.features?.xapi?.credentials?.lrsPassword
                      }
                      control={form.control}
                      showErrors
                    />
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <FormInputRow
                  title='Statement Template'
                  description='The template would define the structure of the xAPI statements.'
                  smallFormTitles
                  InputComponent={
                    <FormControl fullWidth>
                      <InputLabel id='statement-template-label'>
                        Statement Template
                      </InputLabel>
                      <Select
                        labelId='statement-template-label'
                        name='statementTemplate'
                        value={statementTemplate}
                        data-test='statement-template-select'
                        label='Statement Template'
                        onChange={(e) => setStatementTemplate(e.target.value)}
                      >
                        <MenuItem value='mt3'>MT3 (Default)</MenuItem>
                        <MenuItem value='prime'>Prime</MenuItem>
                        <MenuItem value='helix'>Helix</MenuItem>
                      </Select>
                    </FormControl>
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <FormInputRow
                  title='Add SSO attributes to statements'
                  description='When enabled, SSO attributes would be added to the xAPI statements.'
                  smallFormTitles
                  InputComponent={
                    <Checkbox
                      name='addSSOAttributesToStatement'
                      data-test='add-sso-attributes-to-statement-checkbox'
                      checked={sendSSOAttributes}
                      onChange={(e) => setSendSSOAttributes(e.target.checked)}
                    />
                  }
                />
              </Grid>
            </Box>
          </Collapse>
          <Divider style={{ marginBottom: spacing(1) }} />

          <Box display='flex' justifyContent='flex-end' mt={1} mb={1}>
            <ActionButton
              onClick={handleSave}
              disabled={disableSave}
              variant='primary'
              style={{ height: 'min-content' }}
            >
              Save
            </ActionButton>
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}

export default XapiSettings
